<template>
  <Teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-[10000] overflow-hidden">
      <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" @click="close"></div>
      <section class="absolute inset-y-0 right-0 flex w-full max-w-2xl flex-col bg-white shadow-2xl">
        <header class="flex items-center justify-between border-b border-slate-200 px-5 py-4 sm:px-6">
          <div class="flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
              <ClipboardDocumentListIcon class="h-5 w-5 text-blue-600" />
            </div>
            <div class="min-w-0">
              <h3 class="truncate text-lg font-semibold text-slate-900">从待办添加</h3>
              <p class="mt-0.5 text-sm text-slate-500">仅选择待办，不会修改待办状态。</p>
            </div>
          </div>
          <button type="button" class="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" title="关闭" @click="close">
            <XMarkIcon class="h-5 w-5" />
          </button>
        </header>

        <div class="border-b border-slate-200 bg-slate-50 px-5 py-4 sm:px-6">
          <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <label>
              <span class="form-label">状态</span>
              <el-select v-model="filters.status" class="form-select form-control-sm mt-1 w-full" @change="loadTodos">
                <el-option label="全部" value="" />
                <el-option label="待办" value="0" />
                <el-option label="进行中" value="4" />
                <el-option label="已逾期" value="2" />
              </el-select>
            </label>
            <label>
              <span class="form-label">优先级</span>
              <el-select v-model="filters.priority" class="form-select form-control-sm mt-1 w-full" @change="loadTodos">
                <el-option label="全部" value="" />
                <el-option label="低" value="1" />
                <el-option label="中" value="2" />
                <el-option label="高" value="3" />
                <el-option label="紧急" value="4" />
              </el-select>
            </label>
            <label>
              <span class="form-label">关键词</span>
              <input v-model.trim="filters.keyword" class="form-input form-control-sm mt-1 w-full" placeholder="搜索标题" @keyup.enter="loadTodos">
            </label>
            <label>
              <span class="form-label">到期日开始</span>
              <input v-model="filters.dueDateStart" type="date" class="form-input form-control-sm mt-1 w-full" @change="loadTodos">
            </label>
            <label>
              <span class="form-label">到期日结束</span>
              <input v-model="filters.dueDateEnd" type="date" class="form-input form-control-sm mt-1 w-full" @change="loadTodos">
            </label>
            <div class="flex items-end">
              <button type="button" class="btn-secondary w-full" :disabled="loading" @click="loadTodos">
                {{ loading ? '搜索中' : '搜索' }}
              </button>
            </div>
          </div>
        </div>

        <main class="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
          <div v-if="loading" class="flex flex-col items-center gap-3 py-16 text-center">
            <div class="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
            <span class="text-sm text-slate-500">正在加载待办...</span>
          </div>
          <div v-else-if="loadError" class="flex flex-col items-center gap-3 py-16 text-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-full bg-rose-100">
              <ExclamationTriangleIcon class="h-6 w-6 text-rose-500" />
            </div>
            <p class="text-sm text-slate-600">待办加载失败，请重试。</p>
            <button type="button" class="btn-secondary" @click="loadTodos">重试</button>
          </div>
          <div v-else-if="todos.length === 0" class="flex flex-col items-center gap-3 py-16 text-center">
            <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
              <ClipboardDocumentListIcon class="h-6 w-6 text-slate-300" />
            </div>
            <span class="text-sm text-slate-500">暂无可选择的待办</span>
          </div>
          <div v-else class="space-y-2">
            <label v-for="todo in todos" :key="todo.id" class="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 px-4 py-3.5 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50/30 hover:shadow-sm">
              <input v-model="selectedIds" class="form-check mt-0.5" type="checkbox" :value="todo.id">
              <span class="min-w-0 flex-1">
                <span class="block break-words text-sm font-medium text-slate-800">{{ todo.title }}</span>
                <span class="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                  <span class="inline-flex items-center gap-1">
                    <span class="h-1.5 w-1.5 rounded-full bg-slate-300"></span>
                    状态：{{ workReportTodoStatusLabel(todo.status) }}
                  </span>
                  <span class="inline-flex items-center gap-1">
                    <span class="h-1.5 w-1.5 rounded-full bg-slate-300"></span>
                    优先级：{{ workReportPriorityLabel(todo.priority) }}
                  </span>
                  <span class="inline-flex items-center gap-1">
                    <span class="h-1.5 w-1.5 rounded-full bg-slate-300"></span>
                    到期：{{ todo.dueDate || '--' }}
                  </span>
                </span>
              </span>
            </label>
          </div>
        </main>

        <footer class="flex items-center justify-between border-t border-slate-200 bg-slate-50/80 px-5 py-4 sm:px-6">
          <span class="rounded-md bg-blue-50 px-2.5 py-1 text-sm font-medium text-blue-700">已选择 {{ selectedIds.length }} 项</span>
          <div class="flex gap-2">
            <button type="button" class="btn-secondary" @click="close">取消</button>
            <button type="button" class="btn-primary" :disabled="selectedIds.length === 0" @click="confirm">添加</button>
          </div>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { reactive, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { ClipboardDocumentListIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { getReportableTodos } from '@/api/workReport'
import {
  normalizeReportTodoSnapshot,
  normalizeTodoStatusCode,
  workReportPriorityLabel,
  workReportTodoStatusLabel
} from '../workReportUtils'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  selectedTodoIds: { type: Array, default: () => [] }
})
const emit = defineEmits(['update:modelValue', 'confirm'])

const loading = ref(false)
const loadError = ref(false)
const todos = ref([])
const selectedIds = ref([])
const filters = reactive({
  status: '',
  priority: '',
  keyword: '',
  dueDateStart: '',
  dueDateEnd: ''
})
const REPORTABLE_TODO_STATUS_CODES = new Set([0, 2, 4])

watch(() => props.modelValue, (visible) => {
  if (!visible) return
  selectedIds.value = [...props.selectedTodoIds]
  loadTodos()
})

async function loadTodos() {
  loading.value = true
  loadError.value = false
  try {
    const result = await getReportableTodos(buildReportableTodoQuery())
    const records = Array.isArray(result)
      ? result
      : (result?.records || result?.list || result?.rows || result?.data || [])
    todos.value = records
      .map(normalizePickerTodo)
      .filter(Boolean)
      .filter(matchesSelectedStatus)
  } catch {
    loadError.value = true
    ElMessage.error('待办加载失败，请重试')
  } finally {
    loading.value = false
  }
}

function buildReportableTodoQuery() {
  return {
    priority: filters.priority || undefined,
    keyword: filters.keyword || undefined,
    dueDateStart: filters.dueDateStart ? `${filters.dueDateStart}T00:00:00` : undefined,
    dueDateEnd: filters.dueDateEnd ? `${filters.dueDateEnd}T23:59:59` : undefined
  }
}

function normalizePickerTodo(todo) {
  const snapshot = normalizeReportTodoSnapshot(todo)
  if (!snapshot?.todoId || !REPORTABLE_TODO_STATUS_CODES.has(normalizeTodoStatusCode(snapshot.status))) {
    return null
  }

  return {
    ...todo,
    id: snapshot.todoId,
    todoId: snapshot.todoId,
    title: snapshot.title,
    description: snapshot.description,
    priority: snapshot.priority,
    dueDate: snapshot.dueDate,
    status: snapshot.status,
    completeTime: snapshot.completeTime,
    updateTime: snapshot.updateTime
  }
}

function matchesSelectedStatus(todo) {
  if (!filters.status) return true
  return normalizeTodoStatusCode(todo.status) === Number(filters.status)
}

function close() {
  emit('update:modelValue', false)
}

function confirm() {
  const selected = todos.value.filter((todo) => selectedIds.value.includes(todo.id))
  emit('confirm', selected)
  close()
}
</script>
